<template>
    <b-container fluid class="overview">
        <h1 class="text-left">{{ $t('menuItemUserGroups') }}</h1>
        <br />
        <div class="row">
            <!-- Search field -->
            <search-box
                id="searchUserGroups"
                @input="$_onInput"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
            >
            </search-box>
            <x-select
                :id="`profileBackground`"
                :name="'profileBackground'"
                :options="profileBackgroundOptions"
                class="pr-3"
                :textDefaultOption="`${$t('filterOn')} ${$t(
                    'labelProfileBaseLayer'
                ).toLowerCase()}`"
                v-model="selectedBackgroundProfile"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <x-select
                :id="`profileTheme`"
                :name="'profileTheme'"
                class="pr-3"
                :options="profileThemeOptions"
                :textDefaultOption="`${$t('filterOn')} ${$t(
                    'labelProfileTheme'
                ).toLowerCase()}`"
                v-model="selectedThemeProfile"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <x-select
                :id="`profileApi`"
                :name="'profileApi'"
                :options="profileApiOptions"
                :textDefaultOption="`${$t('filterOn')} ${$t(
                    'labelProfileApi'
                )}`"
                v-model="selectedApiProfile"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <b-link class="pl-3 pr-5 pt-2" @click="$_clearFilters()">
                {{ $t('clearFilters') }}
            </b-link>
        </div>
        <!-- Overview Table -->
        <div>
            <b-table
                striped
                hover
                small
                outlined
                bordered
                v-show="dataLoaded"
                @row-clicked="$_onDetails"
                :tbody-tr-class="$_rowClass"
                :items="filteredRecords"
                :fields="fields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                sort-icon-left
            >
            </b-table>
        </div>
        <!-- No content for overview -->
        <div v-show="noData" class="col-md-12">
            <span>{{ $t('userGroupNonePresent') }}</span>
        </div>
        <router-links-footer :routerLinks="[{ name: 'userGroupDetailNew' }]" />
    </b-container>
</template>

<script>
    import SearchBox from '../../components/SearchBox.vue';
    import RouterLinksFooter from '../../components/dashboard/RouterLinksFooter.vue';
    import { nprogress, convertToValueTextPair } from '../../services/utilities';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import { PROFILE_TYPE } from '../../constants';

    export default {
        mixins: [modalMixin],
        name: 'UserGroupsOverview',

        components: {
            'search-box': SearchBox,
            'x-select': XSelect,
            'router-links-footer': RouterLinksFooter
        },

        data: function () {
            return {
                sortBy: 'userName',
                sortDesc: false,
                fields: [
                    {
                        key: 'name',
                        label: this.$t('labelUserGroupname'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle'
                    },
                    {
                        key: 'backgroundProfiles',
                        label: this.$t('labelProfileBaseLayer'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle'
                    },
                    {
                        key: 'themeProfiles',
                        label: this.$t('labelProfileTheme'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle'
                    },
                    {
                        key: 'apiProfiles',
                        label: this.$t('labelProfileApi'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle'
                    }
                ],
                records: [],
                recordsBackground: [],
                recordsTheme: [],
                recordsApi: [],
                searchText: '',
                selectedBackgroundProfile: null,
                selectedThemeProfile: null,
                selectedApiProfile: null,
                isResolved: false
            };
        },

        computed: {
            dataLoaded() {
                return this.records.length > 0;
            },
            noData() {
                return this.isResolved && this.records && this.records.length === 0;
            },
            filteredRecords() {
                let records = this.records;
                // filter records on user groupname
                if (this.searchText && this.searchText.length > 0) {
                    const tokens = this.searchText.toLowerCase();
                    records = records.filter((record) => {
                        return record.name.toLowerCase().includes(tokens);
                    });
                }
                // filter records on backgroundprofile
                records = records.filter((record) => {
                    if (!this.selectedBackgroundProfile) {
                        return true;
                    }

                    if (!record.backgroundProfiles) {
                        return false;
                    }

                    return record.backgroundProfiles.includes(
                        this.selectedBackgroundProfile
                    );
                });

                // filter records on themeprofile
                records = records.filter((record) => {
                    if (!this.selectedThemeProfile) {
                        return true;
                    }

                    if (!record.themeProfiles) {
                        return false;
                    }

                    return record.themeProfiles.includes(this.selectedThemeProfile);
                });

                // filter records on apiprofile
                records = records.filter((record) => {
                    if (!this.selectedApiProfile) {
                        return true;
                    }

                    if (!record.apiProfiles) {
                        return false;
                    }

                    return record.apiProfiles.includes(this.selectedApiProfile);
                });

                return records;
            },
            profileBackgroundOptions() {
                return convertToValueTextPair(
                    this.recordsBackground,
                    'name',
                    'name'
                );
            },
            profileThemeOptions() {
                return convertToValueTextPair(this.recordsTheme, 'name', 'name');
            },
            profileApiOptions() {
                return convertToValueTextPair(this.recordsApi, 'name', 'name');
            }
        },

        async created() {
            await this.$_init();
        },
        methods: {
            $_onDetails(record) {
                this.$router.push({
                    path: `/dashboard/userGroup/detail/${record.id}`
                });
            },
            $_onInput(searchText) {
                this.searchText = searchText;
            },
            $_rowClass() {
                return 'tablerow';
            },

            $_clearFilters() {
                this.$_onInput('');
                this.selectedBackgroundProfile = '';
                this.selectedThemeProfile = '';
                this.selectedApiProfile = '';
            },
            $_getProfileList(apis, userProfiles) {
                if (!apis) {
                    return [];
                }

                // Select apiprofiles that are in de usergroup profiles.
                // Make a comma separated string of their names
                return apis
                    .reduce((a, o) => {
                        if (userProfiles.some((u) => u.profileId === o.id)) {
                            a.push(o.name);
                        }
                        return a;
                    }, [])
                    .sort()
                    .join(', ');
            },
            async $_init() {
                nprogress.start();
                // get all data from the backend
                let result = await this.$store.dispatch('useApiData', {
                    name: 'GetUserGroups'
                });

                this.records = result.data;

                // Only background profiles with id (profile_type_id) == 1
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetProfileOverview',
                    parameters: {
                        profileTypeId: PROFILE_TYPE.BASELAYER
                    }
                });
                this.recordsBackground = result.data;

                // Only theme and storage layer profiles with id (profile_type_id) == 2
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetProfileOverview',
                    parameters: {
                        profileTypeId: PROFILE_TYPE.THEME_FEEDBACK
                    }
                });
                this.recordsTheme = result.data;

                // Only api profiles with id (profile_type_id) == 3
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetProfileOverview',
                    parameters: {
                        profileTypeId: PROFILE_TYPE.API
                    }
                });
                this.recordsApi = result.data;

                // For each record make split profiles into the three categories
                this.records.forEach((record) => {
                    if (record.userGroupProfiles) {
                        record.backgroundProfiles = this.$_getProfileList(
                            this.recordsBackground,
                            record.userGroupProfiles
                        );
                        record.apiProfiles = this.$_getProfileList(
                            this.recordsApi,
                            record.userGroupProfiles
                        );

                        record.themeProfiles = this.$_getProfileList(
                            this.recordsTheme,
                            record.userGroupProfiles
                        );
                    }
                });

                nprogress.done();
                this.isResolved = true;
            }
        }
    };
</script>
